import {useCallback, useEffect, useMemo, useRef} from 'react';

import {Box} from '@mui/material';
import DOMPurify from 'dompurify';
import {useTranslation} from 'react-i18next';

import FuseSvgIcon from '@/@fuse/core/FuseSvgIcon';
import {AUTH_ROLES} from '@/app/auth/authRoles';
import {MiaLoadingAvatarV1} from '@/app/main/split-bills/styles/constants';
import {AppButton, AppButtonVariant} from '@/app/shared-components/AppButton';
import {WebViewService} from '@/services/webview/WebViewService';
import {COLORS} from '@/styles/colors';

import {WEB_VIEW_MESSAGES} from '../constants';
import {
  MessageButtons,
  MessageFollowUpOption,
  SplitBillResult,
} from '../interfaces';
import '../styles';

import {BubbleTailIcon} from './BubbleTailIcon';
import {SplitResultCard} from './SplitResultCard';

const BOLD_REGEX = /\*\*(.*?)\*\*/g;
const BOLD_REPLACEMENT = '<strong>$1</strong>';

const URL_REGEX = /(https?:\/\/[^\s<]+|www\.[^\s<]+?)(?=[.,!?;:]*\s|$)/g;

const HTML_ESCAPE_REGEX = /[&<>"']/g;
const HTML_ESCAPE_MAP: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

function escapeHtml(text: string): string {
  return text.replace(HTML_ESCAPE_REGEX, char => HTML_ESCAPE_MAP[char]);
}

// The only tags/attributes formatBotText() ever generates itself. Anything
// else surviving from the (already-escaped) assistant text is stripped by
// sanitizeBotHtml() below, as a defense-in-depth backstop.
const ALLOWED_TAGS = ['strong', 'a', 'ul', 'li', 'p'];
const ALLOWED_ATTR = ['href', 'target', 'rel'];

export function formatBotText(text: string): string {
  // 0. Escape any raw HTML/JS in the source text (e.g. Mo Assistant output)
  // before we inject our own markup below. Prevents XSS via dangerouslySetInnerHTML.
  const safeText = escapeHtml(text);

  // 1. Bold
  let formatted = safeText.replace(BOLD_REGEX, BOLD_REPLACEMENT);

  // 2. Links - only if not inside an <a> tag
  formatted = formatted.replace(
    URL_REGEX,
    (match, _group, offset, fullText) => {
      // URL inside an <a /> tag?
      const before = fullText.slice(0, offset);
      const openTag = before.lastIndexOf('<a');
      const closeTag = before.lastIndexOf('</a>');

      if (openTag > closeTag) {
        // We are into an <a /> , no replacement
        return match;
      }

      const url = match.startsWith('http') ? match : `https://${match}`;
      return `<a href="${url}" target="_blank" rel="noopener noreferrer">${match}</a>`;
    },
  );

  // 3. Lists and paragraphs
  const lines = formatted.split('\n');
  let html = '';
  let inList = false;

  for (const line of lines) {
    if (line.trim().startsWith('- ')) {
      if (!inList) {
        html += '<ul>';
        inList = true;
      }
      html += `<li>${line.trim().slice(2)}</li>`;
    } else {
      if (inList) {
        html += '</ul>';
        inList = false;
      }
      html += `<p>${line.trim()}</p>`;
    }
  }

  if (inList) html += '</ul>';

  return html;
}

// Final backstop before dangerouslySetInnerHTML: even though formatBotText()
// already HTML-escapes the raw assistant text before generating markup, run
// the result through DOMPurify's strict allowlist too, so a future change to
// the regex-based formatting above can't reopen an HTML-injection path.
export function sanitizeBotHtml(text: string): string {
  return DOMPurify.sanitize(formatBotText(text), {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
  });
}

interface BotMessageBubbleProps {
  text: string;
  result?: unknown;
  splitBill?: SplitBillResult;
  onButtonPress: () => void;
  onClearConversationPress: () => void;
  onSplitAdd?: () => void;
  onSplitUndo?: () => void;
  isCreatingSplit?: boolean;
  splitCreateError?: boolean;
  isSplitAdded?: boolean;
  showSupport?: boolean;
  onButtonSupportPress?: () => void;
  onButtonWalkThroughPress?: () => void;
  isSupportLoading?: boolean;
  buttons?: MessageButtons;
  onButtonLinkPress?: (link: string) => void;
  onFollowUpOptionPress?: (option: MessageFollowUpOption) => void;
  selectedInMessageOption?: string;
}

export const BotMessageBubble = ({
  text,
  result,
  splitBill,
  onButtonPress,
  onClearConversationPress,
  onSplitAdd,
  onSplitUndo,
  isCreatingSplit,
  splitCreateError,
  isSplitAdded,
  showSupport,
  onButtonSupportPress,
  onButtonWalkThroughPress,
  isSupportLoading,
  buttons,
  onButtonLinkPress,
  onFollowUpOptionPress,
  selectedInMessageOption,
}: BotMessageBubbleProps) => {
  const {t} = useTranslation();
  const messageContentRef = useRef<HTMLDivElement | null>(null);

  const isTeen = (): boolean => {
    const userRole = WebViewService.getUserRole();
    return userRole === AUTH_ROLES.teen[0];
  };

  const hasDeepLinkButtons = useMemo(
    () => Boolean(buttons?.links?.length),
    [buttons?.links?.length],
  );
  const hasInMessageOptions = useMemo(
    () => Boolean(buttons?.inMessageOptions?.length),
    [buttons?.inMessageOptions?.length],
  );
  const hasOtherOptions = useMemo(
    () => Boolean(buttons?.otherOptions?.length),
    [buttons?.otherOptions?.length],
  );
  const handleFollowUpOptionPress = useCallback(
    (option: MessageFollowUpOption) => {
      onFollowUpOptionPress?.(option);
    },
    [onFollowUpOptionPress],
  );

  useEffect(() => {
    const messageContainer = messageContentRef.current;
    if (!messageContainer) return;

    const handleLinkClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest('a[href*="modakmakers.com/support"]');

      if (link) {
        e.preventDefault();
        e.stopPropagation();

        if (
          WebViewService.isWebViewActive() &&
          typeof window.ReactNativeWebView !== 'undefined' &&
          window.ReactNativeWebView
        ) {
          window.ReactNativeWebView.postMessage(
            JSON.stringify({
              status: WEB_VIEW_MESSAGES.OPEN_FRESH_CHAT,
            }),
          );
        } else {
          window.open((link as HTMLAnchorElement).href, '_blank');
        }
      }
    };

    messageContainer.addEventListener('click', handleLinkClick);

    return () => {
      messageContainer.removeEventListener('click', handleLinkClick);
    };
  }, [text]);

  const renderChatBubbleContent = useCallback(() => {
    if (splitBill) {
      return (
        <SplitResultCard
          splitBill={splitBill}
          onAddSplit={onSplitAdd}
          onUndo={onSplitUndo}
          isCreatingSplit={isCreatingSplit}
          splitCreateError={splitCreateError}
          isSplitAdded={isSplitAdded}
        />
      );
    }

    return (
      <>
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: -4,
            transform: 'scaleX(-1)',
            color: COLORS.TAIL_BOT,
          }}>
          <BubbleTailIcon />
        </Box>
        {text && (
          <span dangerouslySetInnerHTML={{__html: sanitizeBotHtml(text)}} />
        )}

        {hasDeepLinkButtons && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              marginTop: 12,
              marginBottom: 8,
            }}>
            {buttons.links.map(button => (
              <AppButton
                key={`${button.text}-${button.link}`}
                label={button.text}
                variant={AppButtonVariant.onQuinary}
                iconRight={
                  <FuseSvgIcon
                    size={18}
                    sx={{color: COLORS.ON_SURFACE}}>
                    heroicons-outline:arrow-up-right
                  </FuseSvgIcon>
                }
                onClick={() => onButtonLinkPress?.(button.link)}
              />
            ))}
          </div>
        )}

        {hasInMessageOptions && (
          <div className="chat-follow-up-in-message-options">
            {buttons.inMessageOptions.map(option => {
              const isSelected = selectedInMessageOption === option.label;
              const isDisabled = !!selectedInMessageOption;

              return (
                <div
                  className={`chat-follow-up-in-message-option${isSelected ? ' selected' : isDisabled ? ' dimmed' : ''}`}
                  key={`${option.label}-${option.action}`}>
                  <AppButton
                    label={option.label}
                    variant={AppButtonVariant.onQuaternary}
                    onClick={() =>
                      !isDisabled && handleFollowUpOptionPress(option)
                    }
                    borderColor={isSelected ? COLORS.ON_SURFACE : COLORS.GRAY}
                  />
                </div>
              );
            })}
          </div>
        )}

        {showSupport && (
          <>
            <p style={{marginBottom: 12}}>
              {t('split_bills_page.bot_typing_support')}
            </p>
            <div style={{marginBottom: 8}}>
              <AppButton
                id="chatbot-support-button"
                color={isSupportLoading ? COLORS.GRAY : COLORS.BLACK}
                label={
                  isSupportLoading
                    ? t('split_bills_page.processing_support')
                    : t('split_bills_page.get_support')
                }
                variant={AppButtonVariant.onPrimary}
                onClick={onButtonSupportPress}
                isLoading={isSupportLoading}
              />
            </div>
            {!isTeen() && (
              <AppButton
                id="chatbot-walk-through-app-button"
                label={t('split_bills_page.walk_through_app')}
                variant={AppButtonVariant.onQuinary}
                onClick={onButtonWalkThroughPress}
              />
            )}
          </>
        )}

        {!!result && !splitBill && (
          <div className="chat-bubble-feedback">
            <AppButton
              id="chatbot-clear-conversation"
              removeStyles={true}
              label={t('split_bills_page.clear_conversation')}
              variant={AppButtonVariant.onQuinary}
              onClick={onClearConversationPress}
            />
            <AppButton
              id="chatbot-give-feedback-button"
              removeStyles={true}
              label={t('split_bills_page.give_feedback_button')}
              variant={AppButtonVariant.onQuinary}
              onClick={onButtonPress}
            />
          </div>
        )}
      </>
    );
  }, [
    splitBill,
    text,
    hasDeepLinkButtons,
    buttons?.links,
    buttons?.inMessageOptions,
    hasInMessageOptions,
    showSupport,
    t,
    isSupportLoading,
    onButtonSupportPress,
    onButtonWalkThroughPress,
    result,
    onClearConversationPress,
    onButtonPress,
    onSplitAdd,
    onSplitUndo,
    isCreatingSplit,
    splitCreateError,
    isSplitAdded,
    onButtonLinkPress,
    handleFollowUpOptionPress,
    selectedInMessageOption,
  ]);

  return (
    <>
      <div className="chat-bubble-wrapper bot">
        <img
          src={MiaLoadingAvatarV1}
          alt="Mia avatar"
          className="chat-bubble-avatar"
        />

        <div className="chat-bubble-block">
          <div className="chat-bubble-label">{t('split_bills_page.mia')}</div>
          <div
            className="chat-bubble bot"
            ref={messageContentRef}>
            {renderChatBubbleContent()}
          </div>
        </div>
      </div>
      {hasOtherOptions && (
        <div className="chat-follow-up-other-wrapper">
          {buttons.otherOptions.map(option => {
            const isSelected = selectedInMessageOption === option.label;
            const isDisabled = !!selectedInMessageOption;

            return (
              <div
                className={`chat-follow-up-other-option${isSelected ? ' selected' : isDisabled ? ' dimmed' : ''}`}
                key={`${option.label}-${option.action}`}>
                <AppButton
                  label={option.label}
                  variant={AppButtonVariant.onQuinary}
                  onClick={() =>
                    !isDisabled && handleFollowUpOptionPress(option)
                  }
                  borderColor={isSelected ? COLORS.ON_SURFACE : COLORS.GRAY}
                />
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};
